import React, { useMemo } from 'react';
import { InReceiptItem } from '../../InReceiptItem';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { StyledSummary, StyledSummaryListItemContainer, styles } from './JoyPurchasedOrder.styles';
import { Box, Divider } from '@withjoy/joykit';
import { ButtonV2, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { FormattedCurrency } from '@shared/utils/currency';
import { useHistory, useTranslation } from '@shared/core';
import type { ItemBaseVariant } from '../../../../../../../../components';
import { GiftNoteV2, OrderNumberMetadata, TrackingMetadata } from '../../../../../../../../components';
import type { RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import type { DateFragment } from '@graphql/fragments/coreTypeDefs.graphql.generated';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { StyledTrackingInfoBox, styles as purchasedItemStyles } from '../PurchasedItem/PurchasedItem.styles';
import { Chat } from './icons';
import { useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import type { SalesOrder } from './JoyPurchasedOrder.types';
import { calcSubtotal } from './JoyPurchasedOrder.utils';
import { useIntercom } from '@shared/core/intercom';
import { RETURN_POLICY_PATH } from '@apps/registry/common/components/Catalog/Catalog.constants';
import { computeRegistryPurchaseInfoCostLines } from '@apps/registry/common/utils/additionalCostLines';
import { useRegistryShippingAndHandlingLineLabel } from '@apps/registry/common/hooks/useRegistryShippingAndHandlingLineLabel';

type Order = { product: Partial<CookedProduct>; quantity: number; price: string; totalPrice: string; siteName?: string | null };

type JoyPurchasedOrderProps = Partial<RegistryOrderFragment> & {
  orderList: Array<Order>;
  salesOrder: SalesOrder;
  isEdelivery?: boolean;
  brand?: string;
  brandText?: string;
  last4?: string;
  variant?: ItemBaseVariant;
};

type FormatDateOptions = {
  format?: string;
};
export const formatDate = (date: Maybe<DateFragment>, options: FormatDateOptions = {}) => {
  const formatString = options.format || 'MMMM d, yyyy';

  const eventDate = date?.milliseconds || new Date();
  const dateWithTimezoneFormatting = utcToZonedTime(eventDate, date?.timezone || 'America/Los_Angeles');
  return format(dateWithTimezoneFormatting, formatString);
};

export const JoyPurchasedOrder = ({ orderList, salesOrder, isEdelivery, brand, brandText, last4, orderedAt, variant, fulfillment, note, id }: JoyPurchasedOrderProps) => {
  const { t } = useTranslation('sharedRegistry');
  const intercom = useIntercom();
  const history = useHistory();
  const { subtotal, salesTax, total, paidNote, items, item, ordered, eCardFee } = t('shoppingCart');
  const { returnPolicy, contactSupport } = t('shoppingCartHelp');
  const registryGiftWrapEcardPrice = useGiftWrapECardPrice();
  const giftWrapPriceAsString = `${registryGiftWrapEcardPrice}`;
  const orderListCount = orderList.reduce((acc, item) => acc + item.quantity, 0);
  const listCountText = `${orderListCount} ${orderListCount === 1 ? item() : items()}`;
  const orderDateText = orderedAt ? `${ordered()} ${formatDate(orderedAt)}` : '';
  // we need to calculate this from the salesOrder object
  const hasGiftWrapCard = false; //hasGiftWrapFromSalesOrderPrice(registryGiftWrapEcardPrice, salesOrder);
  const calculatedSubtotalAsString = `${calcSubtotal(salesOrder)}`;
  const costInfo = useMemo(() => computeRegistryPurchaseInfoCostLines(salesOrder), [salesOrder]);
  const { getDeliveryLineLabel, getSurchargeLineLabel } = useRegistryShippingAndHandlingLineLabel();

  return (
    <Flex
      flexDirection="column"
      backgroundColor={variant === 'normal' ? 'white' : 'trasparent'}
      paddingTop={variant === 'normal' ? 6 : 0}
      paddingBottom={variant === 'normal' ? 6 : 0}
    >
      {orderDateText && (
        <TextV2
          typographyVariant="hed2"
          paddingLeft={5}
        >
          {orderDateText}
        </TextV2>
      )}
      <StyledTrackingInfoBox __css={purchasedItemStyles.trackingInfoBox}>
        {fulfillment?.orderNumber && <OrderNumberMetadata orderNumber={fulfillment.orderNumber} />}
        {fulfillment?.shipments?.[0]?.trackingInformation?.trackingNumber && (
          <Box marginTop={fulfillment?.orderNumber ? 6 : 0}>
            <TrackingMetadata
              trackingNumber={fulfillment.shipments[0].trackingInformation.trackingNumber}
              shippingCarrier={fulfillment.shipments[0].trackingInformation.company || undefined}
            />
          </Box>
        )}
      </StyledTrackingInfoBox>
      {id && note && (
        <GiftNoteV2
          note={note}
          orderId={id}
          variant="solid"
        />
      )}
      <TextV2
        typographyVariant="hed1"
        paddingLeft={5}
      >
        {listCountText}
      </TextV2>
      {orderList?.length > 0 &&
        orderList.map(({ product, quantity, price, totalPrice, siteName }, idx) => (
          <InReceiptItem
            key={idx}
            product={product}
            quantity={quantity}
            price={price}
            totalPrice={totalPrice}
            isDonationFundItem={false}
            siteName={siteName}
            compact
            variant={variant || 'transparent'}
            sm
          />
        ))}
      <StyledSummary __css={styles.summaryStyles}>
        <Divider />
        <SpacingStack
          spacing={3}
          marginY={6}
        >
          <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
            <TextV2 typographyVariant="label2">{subtotal()}</TextV2>
            <TextV2 typographyVariant="label2">
              <FormattedCurrency
                priceFloatingPointDecimalString={calculatedSubtotalAsString}
                formatForm="short"
              />
            </TextV2>
          </StyledSummaryListItemContainer>
          {hasGiftWrapCard && (
            <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
              <TextV2 typographyVariant="label2">{eCardFee()}</TextV2>
              <TextV2 typographyVariant="label2">
                <FormattedCurrency
                  priceFloatingPointDecimalString={giftWrapPriceAsString}
                  priceCurrencyCode="USD"
                  formatForm="short"
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          )}
          {!isEdelivery && (
            <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
              <TextV2 typographyVariant="label2">{getDeliveryLineLabel()}</TextV2>
              <TextV2 typographyVariant="label2">
                <FormattedCurrency
                  priceFloatingPointDecimalString={costInfo?.deliveryTotalValue.floatingPointDecimalString ?? '0'}
                  priceCurrencyCode={costInfo?.deliveryTotalValue.currency?.code}
                  formatForm="short"
                  canShowFreeLabel
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          )}
          {!isEdelivery && costInfo && costInfo.bulkSurchargeTotalValue.valueInMinorUnits > 0 && (
            <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
              <TextV2 typographyVariant="label2">{getSurchargeLineLabel(costInfo.bulkSurchargeShippingLines.length)}</TextV2>
              <TextV2 typographyVariant="label2">
                <FormattedCurrency
                  priceFloatingPointDecimalString={costInfo?.bulkSurchargeTotalValue.floatingPointDecimalString ?? '0'}
                  priceCurrencyCode={costInfo?.bulkSurchargeTotalValue.currency?.code}
                  formatForm="short"
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          )}
          {costInfo?.additionalCostLines.map((line, index) => (
            <StyledSummaryListItemContainer
              key={`costLine-${line.title}-${index}`}
              __css={styles.summaryListItemContainerStyles}
            >
              <TextV2 typographyVariant="label2">{line.title}</TextV2>
              <TextV2 typographyVariant="label2">
                <FormattedCurrency
                  priceFloatingPointDecimalString={line.lineValue?.floatingPointDecimalString ?? '0'}
                  priceCurrencyCode={line.lineValue?.currency?.code}
                  formatForm="short"
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          ))}
          <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
            <TextV2 typographyVariant="label2">{salesTax()}</TextV2>
            <TextV2 typographyVariant="label2">
              <FormattedCurrency
                priceFloatingPointDecimalString={salesOrder?.taxesTotal?.floatingPointDecimalString ?? '0'}
                priceCurrencyCode={salesOrder?.taxesTotal?.currency?.code}
                formatForm="short"
              />
            </TextV2>
          </StyledSummaryListItemContainer>
        </SpacingStack>
        <Divider />
        <StyledSummaryListItemContainer
          __css={styles.summaryListItemContainerStyles}
          paddingY={4}
        >
          <TextV2 typographyVariant="hed1">{total()}</TextV2>
          <TextV2 typographyVariant="hed3">
            <FormattedCurrency
              priceFloatingPointDecimalString={salesOrder?.total?.floatingPointDecimalString ?? '0'}
              priceCurrencyCode={salesOrder?.total?.currency?.code}
              formatForm="short"
            />
          </TextV2>
        </StyledSummaryListItemContainer>
        <Flex>{brand && last4 && <TextV2 typographyVariant="label2">{paidNote({ card: brandText, cardLastDigits: last4 })}</TextV2>}</Flex>
      </StyledSummary>
      {id && !note && (
        <GiftNoteV2
          note={note}
          orderId={id}
          variant="solid"
        />
      )}
      <ButtonV2
        variant="muted"
        marginBottom={6}
        marginX={5}
        onClick={() => intercom.boot().then(() => intercom.show())}
      >
        <Flex marginRight={1}>
          <Chat />
        </Flex>
        {contactSupport()}
      </ButtonV2>
      <ButtonV2
        typographyVariant="button2"
        intent="neutral"
        variant="link"
        marginX={5}
        onClick={() => history.push(RETURN_POLICY_PATH)}
      >
        <Flex flexDirection="column">
          {returnPolicy()}
          <Divider />
        </Flex>
      </ButtonV2>
    </Flex>
  );
};
